import { useGetBreeds } from '@application/api';
import { useShowToast, useValidation } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import type { BreedInterestFormValues } from '@presentation/components';
import { DogBreedInterestBanner } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { EmailContactCreatedFromEnum } from '@gql/graphql';
import { array, bool, object, string } from 'yup';
import { useCreateDogBreedInterestSubscription } from './api/useCreateDogBreedInterestSubscription';
import type { BreedInterestControllerProps } from './types';

export const BreedInterestController: FC<BreedInterestControllerProps> = (props) => {
  const { preselectedBreedName = [] } = props;

  const { translate } = useTranslation();
  const { showToast } = useShowToast();
  const { emailMessage, isValidEmail, requiredMessage } = useValidation();
  const { breedOptionsBasenameKey } = useGetBreeds();
  const { createDogBreedInterestSubscriptionFromForm, createDogBreedInterestSubscriptionLoading } =
    useCreateDogBreedInterestSubscription();

  const form = useFormik<BreedInterestFormValues>({
    enableReinitialize: true,
    validationSchema: object({
      email: string()
        .email(emailMessage)
        .required(requiredMessage)
        .test('is-valid-email', emailMessage, (value) => isValidEmail(value)),
      marketingEmailConsent: bool().oneOf([true], requiredMessage),
      hasADog: bool().required(requiredMessage),
      createdFrom: string().optional().nullable(),
      lookingFor: string().optional().nullable(),
      sensitivities: array()
        .of(string())
        .when('hasADog', {
          is: true,
          then: (schema) => schema.required(requiredMessage),
          otherwise: (schema) => schema.optional().nullable(),
        }),
    }),
    initialValues: {
      // Some users navigate to http://localhost:8500/be/fr/chiens-a-vendre/unkown-race creating
      // this bug https://tipaw.atlassian.net/browse/TC-1313
      dogBreedNames: preselectedBreedName.filter((name) =>
        breedOptionsBasenameKey.find((x) => x.value === name),
      ),
      email: '',
      marketingEmailConsent: false,
      hasADog: undefined,
      createdFrom: EmailContactCreatedFromEnum.BREED_INTEREST,
      lookingFor: 'puppy',
      sensitivities: undefined,
    },
    onSubmit: async (values, { resetForm }) => {
      await createDogBreedInterestSubscriptionFromForm(values);
      showToast(translate('common.toasts.breedInterestSubscriptionSuccess'), {
        type: 'success',
        autoClose: 1500,
      });
      Analytics.sendSubscribeToDogBreeds(values.dogBreedNames);
      resetForm();
    },
  });

  return (
    <FormikProvider value={form}>
      <DogBreedInterestBanner
        breedOptions={breedOptionsBasenameKey}
        isSubmitting={createDogBreedInterestSubscriptionLoading}
      />
    </FormikProvider>
  );
};
